/**
 * Render cloud-fraction overlay as a JITTERED GRID of grey cloud icons inside
 * each contiguous layer where cldfra >= 0.2. Matches Skysight's grey-cloud-icons
 * group decoded from docs/skysight-skewt-bundle.js (the `d.forEach(a => {…})`
 * inside the cldfra rendering).
 *
 * Algorithm:
 *   1. Walk data, group consecutive slices with cldfra >= THRESHOLD into layers.
 *   2. Layers with only one slice are skipped (need real thickness).
 *   3. For each layer:
 *      - Horizontal extent  i = x(tMin + maxCldfra*10) - x(tMin)
 *        (so a 100%-cover layer fills the full 10°C cloud-axis width)
 *      - Row count          f = ceil(layerThicknessPx / ROW_STEP), >= 1
 *      - For each row b ∈ [0, f):
 *          icon count  T = max(1, round( ceil(i / COL_STEP) * pow(meanCldfra, 1.5) ))
 *          icon step   S = i / T
 *          opacity     = 0.4 + meanCldfra * 0.3
 *          Each icon at:  X = u + C*S + h/2 + (b%2 ? h*0.3 : 0)
 *                         Y = layerTopY + b*ROW_STEP + (C%3)*8
 *
 * Source path comes from Skysight bundle (a different shape than the cumulus
 * icon — softer puffy cloud with quadratic curves):
 *   M25,60 Q10,60 10,47.5 T10,22.5 Q10,10 22.5,10 Q32.5,0 42.5,0
 *   Q53,0 60,7.5 T70,22.5 Q80,22.5 85,30 T90,45 Q90,55 80,57.5 T55,60Z
 *
 * @param {import('d3-selection').Selection} container
 * @param {import('../skewt/scales.js').Scales} scales
 * @param {Array<{press, cldfra}>} data
 */

const THRESHOLD = 0.2;
const ICON_PATH =
    'M25,60 Q10,60 10,47.5 T10,22.5 Q10,10 22.5,10 Q32.5,0 42.5,0 ' +
    'Q53,0 60,7.5 T70,22.5 Q80,22.5 85,30 T90,45 Q90,55 80,57.5 T55,60Z';
const ICON_NATURAL_W = 88;
const ICON_SIZE = 50;             // Skysight's `h`
const ROW_STEP = ICON_SIZE * 0.35;  // ≈ 17.5 — vertical spacing
const COL_STEP = ICON_SIZE * 0.35;  // ≈ 17.5 — horizontal spacing
const SCALE = ICON_SIZE / ICON_NATURAL_W;

export function drawCldFraOverlay(container, scales, data) {
    container.selectAll('.skewt-ext-cldfra').remove();
    if (!data || data.length === 0) return;

    const layer = container.append('g').attr('class', 'skewt-ext-cldfra');

    // 1) Detect contiguous layers above threshold.
    const layers = [];
    let buf = [];
    for (const slice of data) {
        const cf = Number(slice.cldfra);
        if (Number.isFinite(cf) && cf >= THRESHOLD) {
            buf.push(slice);
        } else if (buf.length > 1) {
            layers.push({ data: buf });
            buf = [];
        } else {
            buf = [];
        }
    }
    if (buf.length > 1) layers.push({ data: buf });
    if (layers.length === 0) return;

    const u = scales.x(scales.tMin);

    // 2) For each layer, place a jittered grid of cloud icons.
    for (const lyr of layers) {
        const pStart = lyr.data[0].press;             // highest press → bottom of layer
        const pEnd = lyr.data[lyr.data.length - 1].press; // lowest press → top of layer
        const yBottom = scales.y(pStart);              // larger y (lower on chart)
        const yTop = scales.y(pEnd);                   // smaller y (higher on chart)
        const m = yBottom - yTop;                      // layer thickness in px

        const meanCldfra =
            lyr.data.reduce((acc, p) => acc + p.cldfra, 0) / lyr.data.length;
        const maxCldfra = Math.max(...lyr.data.map((p) => p.cldfra));

        // Horizontal extent — wider for higher cover.
        const i = scales.x(scales.tMin + maxCldfra * 10) - u;
        if (i <= 0) continue;

        const rows = Math.max(1, Math.ceil(m / ROW_STEP));

        for (let b = 0; b < rows; b++) {
            const rowY = yTop + b * ROW_STEP;
            if (rowY > yBottom + ICON_SIZE / 2) continue;

            const L = Math.ceil(i / COL_STEP);
            const coverWeight = Math.pow(meanCldfra, 1.5);
            const colCount = Math.max(1, Math.round(L * coverWeight));
            const step = i / Math.max(colCount, 1);

            for (let C = 0; C < colCount; C++) {
                const xJitter = (b % 2) * (ICON_SIZE * 0.3);
                const yJitter = (C % 3) * 8;
                const X = u + C * step + ICON_SIZE / 2 + xJitter;
                const Y = rowY + yJitter;

                const g = layer
                    .append('g')
                    .attr('transform', `translate(${X}, ${Y})`);
                g.append('path')
                    .attr('d', ICON_PATH)
                    .attr(
                        'transform',
                        `translate(-44, -40) scale(${SCALE})`,
                    )
                    .style('fill', '#d0d0d0')
                    .style('stroke', '#888')
                    .style('stroke-width', 1.2)
                    .style('opacity', 0.4 + meanCldfra * 0.3)
                    .style('vector-effect', 'non-scaling-stroke')
                    .attr('pointer-events', 'none');
            }
        }
    }
}
